/**
 * ROAS Recovery Curve Benchmark Analysis - 核心算法
 * 从 roas-analyze.py 移植，用于前端 cohort 基准分析
 */

const VERSION = 3

const DAY_COLS = [0, 3, 7, 14, 30, 60, 90, 120, 150, 180]

/**
 * 将 API dailyData 转换为 cohort 结构
 * @param {Array} dailyData - API 返回的 dailyData 数组
 * @param {Function} shouldExclude - (date) => boolean 日期排除函数
 * @returns {Array} cohort 数组，每项含 date, date_str, revenue, cost, installs, cpi, latest_roas, day_roas
 */
export function transformDailyData(dailyData, shouldExclude = () => false) {
  if (!Array.isArray(dailyData) || dailyData.length === 0) return []

  const cohorts = []
  for (const item of dailyData) {
    const installDate = item.install_date
    if (!installDate) continue

    const cost = Number(item.cost) || 0
    if (cost <= 0) continue

    const dateObj = parseInstallDate(installDate)
    if (!dateObj) continue
    if (shouldExclude(dateObj)) continue

    const revenue = Number(item.revenue) || 0
    const installs = Number(item.installs) || 0
    const cpi = installs > 0 ? cost / installs : null
    const ltv = installs > 0 ? revenue / installs : null
    const latestRoas = item.roas != null ? Number(item.roas) : null

    const dayRoas = {}
    const days = item.days || {}
    const dayCosts = item.dayCosts || {}

    // 始终根据实际每日收入累加计算 ROAS
    // 这样确保只有真的有完整数据的 cohort 才会有对应 milestone 的 ROAS
    for (const day of DAY_COLS) {
      const dayKey = `day${day}`

      // 检查是否真的有该 milestone 的每日数据
      // 逻辑：对于 Day N，必须 days 中包含 dayN 这个 key（即真的有该天的收入数据）
      if (!Object.prototype.hasOwnProperty.call(days, dayKey)) {
        continue
      }

      // 累加每日收入
      let cumRevenue = 0
      let hasAllDays = true
      for (let i = 0; i <= day; i++) {
        const dKey = `day${i}`
        if (Object.prototype.hasOwnProperty.call(days, dKey)) {
          const val = Number(days[dKey])
          if (!Number.isNaN(val)) {
            cumRevenue += val
          }
        } else if (i < day) {
          // 中间某天缺失，认为数据不完整
          hasAllDays = false
        }
      }

      // 数据不完整或没有收入则不计算
      if (!hasAllDays || cumRevenue <= 0) continue

      // 获取该 day 对应的 cost
      const dayCost = dayCosts[dayKey] !== undefined ? Number(dayCosts[dayKey]) : cost
      if (dayCost <= 0) continue

      dayRoas[day] = (cumRevenue / dayCost) * 100
    }

    cohorts.push({
      date: dateObj,
      date_str: installDate,
      revenue,
      cost,
      installs,
      cpi,
      ltv,
      latest_roas: latestRoas,
      day_roas: dayRoas,
    })
  }
  return cohorts
}

function parseInstallDate(str) {
  if (!str) return null
  const normalized = String(str).replace(/-/g, '')
  if (normalized.length !== 8) return null
  const y = parseInt(normalized.slice(0, 4), 10)
  const m = parseInt(normalized.slice(4, 6), 10) - 1
  const d = parseInt(normalized.slice(6, 8), 10)
  const dt = new Date(y, m, d)
  if (isNaN(dt.getTime())) return null
  return dt
}

/**
 * IQR 异常值过滤
 * @param {Array} cohorts - cohort 数组
 * @param {number} dayKey - 天数，如 0, 3, 7
 * @returns {Array} 过滤后的 cohort 数组
 */
export function iqrFilter(cohorts, dayKey) {
  const vals = cohorts
    .filter((c) => dayKey in (c.day_roas || {}))
    .map((c) => c.day_roas[dayKey])
    .sort((a, b) => a - b)
  if (vals.length < 4) return cohorts
  const n = vals.length
  const q1 = vals[Math.floor(n / 4)]
  const q3 = vals[Math.floor((3 * n) / 4)]
  const iqr = q3 - q1
  const lb = q1 - 1.5 * iqr
  const ub = q3 + 1.5 * iqr
  return cohorts.filter(
    (c) =>
      !(dayKey in (c.day_roas || {})) ||
      (c.day_roas[dayKey] >= lb && c.day_roas[dayKey] <= ub)
  )
}

/**
 * 百分位计算（线性插值）
 * @param {Array<number>} sortedVals - 已排序数组
 * @param {number} k - 百分位 0-100
 * @returns {number}
 */
export function percentile(sortedVals, k) {
  if (!sortedVals || sortedVals.length === 0) return 0
  const n = sortedVals.length
  const idx = (k / 100) * (n - 1)
  const lo = Math.floor(idx)
  const hi = Math.min(lo + 1, n - 1)
  const frac = idx - lo
  return sortedVals[lo] * (1 - frac) + sortedVals[hi] * frac
}

function mean(arr) {
  if (!arr || arr.length === 0) return 0
  return arr.reduce((a, b) => a + b, 0) / arr.length
}

function median(arr) {
  if (!arr || arr.length === 0) return 0
  const sorted = [...arr].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2
}

/**
 * 构建日期排除函数
 * @param {string|null} excludeBefore - YYYY-MM-DD 或 null
 * @param {Array<string>} excludeRanges - ['YYYY-MM-DD:YYYY-MM-DD', ...]
 * @returns {{ shouldExclude: Function, describe: Function }}
 */
export function buildExcludeFn(excludeBefore, excludeRanges = []) {
  const beforeDt = excludeBefore ? parseDateStr(excludeBefore) : null
  const ranges = (excludeRanges || []).map((spec) => {
    const [start, end] = spec.split(':')
    return [parseDateStr(start), parseDateStr(end)]
  })

  const shouldExclude = (dt) => {
    if (beforeDt && dt < beforeDt) return true
    for (const [start, end] of ranges) {
      if (start && end && dt >= start && dt <= end) return true
    }
    return false
  }

  const describe = () => {
    const parts = []
    if (beforeDt) parts.push(`${formatDate(beforeDt)} 之前`)
    for (const [start, end] of ranges) {
      if (start && end) parts.push(`${formatDate(start)}–${formatDate(end)}`)
    }
    parts.push('IQR 异常值')
    return parts.join('、')
  }

  return { shouldExclude, describe }
}

function parseDateStr(s) {
  if (!s || typeof s !== 'string') return null
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10))
  return isNaN(d.getTime()) ? null : d
}

function formatDate(d) {
  if (!d) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Section 1: ROAS 分布计算
 * @returns {Array<{ low: number, high: number, count: number, pct: number, cumPct: number, has100: boolean }>}
 */
export function calcDistribution(cohorts, day, bucketSize = 5) {
  const withData = cohorts.filter((c) => day in (c.day_roas || {}))
  const cleaned =
    withData.length >= 4 ? iqrFilter(iqrFilter(withData, day), 7) : withData
  const vals = cleaned.map((c) => c.day_roas[day]).sort((a, b) => a - b)
  if (vals.length === 0) return []

  const lo = Math.floor(Math.min(...vals) / bucketSize) * bucketSize
  const hi = (Math.floor(Math.max(...vals) / bucketSize) + 1) * bucketSize
  const buckets = []
  for (let edge = lo; edge < hi; edge += bucketSize) {
    const count = vals.filter((v) => v >= edge && v < edge + bucketSize).length
    buckets.push({
      low: edge,
      high: edge + bucketSize,
      count,
      has100: edge <= 100 && 100 < edge + bucketSize,
    })
  }
  let cum = 0
  return buckets.map((b) => {
    cum += b.count
    return {
      ...b,
      pct: (b.count / vals.length) * 100,
      cumPct: (cum / vals.length) * 100,
    }
  })
}

/**
 * Section 5.2: LTV 分布
 */
export function calcLtvBuckets(
  cohorts,
  cTarget,
  targetDay,
  threshold,
  bucketSize = null
) {
  const allLtv = cohorts
    .filter((c) => c.ltv != null)
    .map((c) => c.ltv)
    .sort((a, b) => a - b)
  if (allLtv.length === 0) return []

  const ltvMedian = median(allLtv)
  // 自动判断 bucket size：median < 0.15 使用 0.005，< 0.5 使用 0.01，否则使用 0.02
  const ltvBucket =
    bucketSize ??
    (ltvMedian < 0.15 ? 0.005 : ltvMedian < 0.5 ? 0.01 : 0.02)
  const ltvWithTarget = cTarget.filter((c) => c.ltv != null)
  const ltvLo = Math.floor(Math.min(...allLtv) / ltvBucket) * ltvBucket
  const ltvHi = (Math.floor(Math.max(...allLtv) / ltvBucket) + 1) * ltvBucket
  const buckets = []
  for (let edge = ltvLo; edge < ltvHi; edge += ltvBucket) {
    const nextEdge = Math.round((edge + ltvBucket) * 10000) / 10000
    const count = allLtv.filter((v) => v >= edge && v < nextEdge).length
    const inTarget = ltvWithTarget.filter(
      (c) => c.ltv >= edge && c.ltv < nextEdge
    )
    const nSuccess = inTarget.filter(
      (c) => c.day_roas[targetDay] >= threshold
    ).length
    buckets.push({
      low: edge,
      high: nextEdge,
      count,
      evaluable: inTarget.length,
      success: nSuccess,
      rateStr:
        inTarget.length > 0
          ? `${((nSuccess / inTarget.length) * 100).toFixed(0)}% (${nSuccess}/${inTarget.length})`
          : '–',
    })
  }
  let cum = 0
  return buckets.map((b) => {
    cum += b.count
    return {
      ...b,
      pct: (b.count / allLtv.length) * 100,
      cumPct: (cum / allLtv.length) * 100,
    }
  })
}

/**
 * Section 5.3: 安装量分布
 */
export function calcInstallsBuckets(
  cohorts,
  cTarget,
  targetDay,
  threshold,
  bucketSize = 500
) {
  const allInstalls = cohorts.map((c) => c.installs).sort((a, b) => a - b)
  if (allInstalls.length === 0) return []

  const instWithTarget = cTarget
  const instLo = Math.floor(Math.min(...allInstalls) / bucketSize) * bucketSize
  const instHi = (Math.floor(Math.max(...allInstalls) / bucketSize) + 1) * bucketSize
  const buckets = []
  for (let edge = instLo; edge < instHi; edge += bucketSize) {
    const nextEdge = edge + bucketSize
    const count = allInstalls.filter((v) => v >= edge && v < nextEdge).length
    const inTarget = instWithTarget.filter(
      (c) => c.installs >= edge && c.installs < nextEdge
    )
    const nSuccess = inTarget.filter(
      (c) => c.day_roas[targetDay] >= threshold
    ).length
    buckets.push({
      low: edge,
      high: nextEdge,
      count,
      evaluable: inTarget.length,
      success: nSuccess,
      rateStr:
        inTarget.length > 0
          ? `${((nSuccess / inTarget.length) * 100).toFixed(0)}% (${nSuccess}/${inTarget.length})`
          : '–',
    })
  }
  let cum = 0
  return buckets.map((b) => {
    cum += b.count
    return {
      ...b,
      pct: (b.count / allInstalls.length) * 100,
      cumPct: (cum / allInstalls.length) * 100,
    }
  })
}

/**
 * Section 2: 早期达标基准
 * @returns {Array<{ day: number, threshold: number, mean: number, excellent: number, hitRate: number, hitAbove: number, hitTotal: number, recoverRate: number, recoverOk: number, recoverTotal: number, accuracy: number }>}
 */
export function calcEarlyMilestones(
  cTarget,
  successful,
  failed,
  targetDay,
  threshold,
  earlyDays = [0, 3, 7, 14, 30]
) {
  const result = []
  // cTarget 中的 cohort 都有 Day targetDay 数据
  // 对于每个 earlyDay，只考虑同时有该 earlyDay 数据的 cohort
  for (const ed of earlyDays) {
    // 同时有 earlyDay 和 targetDay 数据的 cohort
    const cohortsWithEarlyDay = cTarget.filter((c) => ed in (c.day_roas || {}))
    const successfulWithEarlyDay = successful.filter((c) => ed in (c.day_roas || {}))

    if (successfulWithEarlyDay.length === 0) continue

    const sVals = successfulWithEarlyDay
      .map((c) => c.day_roas[ed])
      .sort((a, b) => a - b)

    const n = sVals.length
    const lo10 = Math.max(0, Math.floor(n * 0.05))
    const hi10 = Math.max(1, Math.floor(n * 0.15))
    const lo75 = Math.floor(n * 0.7)
    const hi75 = Math.min(n, Math.floor(n * 0.8))
    const thresholdVal = mean(sVals.slice(lo10, hi10)) || sVals[0]
    const meanVal = mean(sVals)
    const excellentVal =
      hi75 > lo75 ? mean(sVals.slice(lo75, hi75)) : sVals[sVals.length - 1]

    const above = cohortsWithEarlyDay.filter(
      (c) => c.day_roas[ed] >= thresholdVal
    )
    const aboveHit = above.filter(
      (c) => c.day_roas[targetDay] >= threshold
    ).length
    const hitRate = above.length > 0 ? (aboveHit / above.length) * 100 : 0

    const below = cohortsWithEarlyDay.filter(
      (c) => c.day_roas[ed] < thresholdVal
    )
    const belowOk = below.filter(
      (c) => c.day_roas[targetDay] >= threshold
    ).length
    const recoverRate =
      below.length > 0 ? (belowOk / below.length) * 100 : 0

    const total = above.length + below.length
    const belowFail = below.length - belowOk
    const accuracy =
      total > 0 ? ((aboveHit + belowFail) / total) * 100 : 0

    result.push({
      day: ed,
      threshold: thresholdVal,
      mean: meanVal,
      excellent: excellentVal,
      hitRate,
      hitAbove: aboveHit,
      hitTotal: above.length,
      recoverRate,
      recoverOk: belowOk,
      recoverTotal: below.length,
      accuracy,
    })
  }
  return result
}

/**
 * Section 3: 回收曲线（全量）
 * @returns {Array<{ day: number, count: number, p10: number, p25: number, p50: number, p75: number, p90: number, mean: number }>}
 */
export function calcRecoveryCurveAll(cohorts, targetDay) {
  const curveDays = [0, 3, 7, 14, 30, 60]
    .filter((d) => d < targetDay)
    .concat([targetDay])
  const result = []
  for (const d of curveDays) {
    const vals = cohorts
      .filter((c) => d in (c.day_roas || {}))
      .map((c) => c.day_roas[d])
      .sort((a, b) => a - b)
    if (vals.length === 0) continue
    result.push({
      day: d,
      count: vals.length,
      p10: percentile(vals, 10),
      p25: percentile(vals, 25),
      p50: percentile(vals, 50),
      p75: percentile(vals, 75),
      p90: percentile(vals, 90),
      mean: mean(vals),
    })
  }
  return result
}

/**
 * Section 4: 回收曲线基准（成功队列）
 * @returns {Array<{ day: number, p10: number, p25: number, p50: number, p75: number, p90: number, hitP25: number, hitP50: number }>}
 */
export function calcRecoveryCurveSuccess(
  successful,
  cTarget,
  targetDay,
  threshold
) {
  const curveDays = [0, 3, 7, 14, 30, 60]
    .filter((d) => d < targetDay)
    .concat([targetDay])
  const result = []
  for (const d of curveDays) {
    const sVals = successful
      .filter((c) => d in (c.day_roas || {}))
      .map((c) => c.day_roas[d])
      .sort((a, b) => a - b)
    if (sVals.length === 0) continue

    const p25Val = percentile(sVals, 25)
    const p50Val = percentile(sVals, 50)

    const calcHitRate = (thresholdVal) => {
      const above = cTarget.filter(
        (c) =>
          d in (c.day_roas || {}) && c.day_roas[d] >= thresholdVal
      )
      const aboveHit = above.filter(
        (c) => c.day_roas[targetDay] >= threshold
      ).length
      return above.length > 0 ? (aboveHit / above.length) * 100 : 0
    }

    result.push({
      day: d,
      p10: percentile(sVals, 10),
      p25: p25Val,
      p50: p50Val,
      p75: percentile(sVals, 75),
      p90: percentile(sVals, 90),
      hitP25: calcHitRate(p25Val),
      hitP50: calcHitRate(p50Val),
    })
  }
  return result
}

/**
 * Section 5.1: CPI 分布
 * @returns {Array<{ low: number, high: number, count: number, pct: number, cumPct: number, evaluable: number, success: number, rateStr: string }>}
 */
export function calcCpiBuckets(
  cohorts,
  cTarget,
  targetDay,
  threshold,
  bucketSize = null
) {
  const allCpi = cohorts
    .filter((c) => c.cpi != null)
    .map((c) => c.cpi)
    .sort((a, b) => a - b)
  if (allCpi.length === 0) return []

  const cpiMedian = median(allCpi)
  // 自动判断 bucket size：median < 0.15 使用 0.005，< 0.5 使用 0.01，否则使用 0.02
  const cpiBucket =
    bucketSize ??
    (cpiMedian < 0.15 ? 0.005 : cpiMedian < 0.5 ? 0.01 : 0.02)
  const cpiWithTarget = cTarget.filter((c) => c.cpi != null)
  const cpiLo = Math.floor(Math.min(...allCpi) / cpiBucket) * cpiBucket
  const cpiHi = (Math.floor(Math.max(...allCpi) / cpiBucket) + 1) * cpiBucket
  const buckets = []
  for (let edge = cpiLo; edge < cpiHi; edge += cpiBucket) {
    const nextEdge = Math.round((edge + cpiBucket) * 10000) / 10000
    const count = allCpi.filter((v) => v >= edge && v < nextEdge).length
    const inTarget = cpiWithTarget.filter(
      (c) => c.cpi >= edge && c.cpi < nextEdge
    )
    const nSuccess = inTarget.filter(
      (c) => c.day_roas[targetDay] >= threshold
    ).length
    buckets.push({
      low: edge,
      high: nextEdge,
      count,
      evaluable: inTarget.length,
      success: nSuccess,
      rateStr:
        inTarget.length > 0
          ? `${((nSuccess / inTarget.length) * 100).toFixed(0)}% (${nSuccess}/${inTarget.length})`
          : '–',
    })
  }
  let cum = 0
  return buckets.map((b) => {
    cum += b.count
    return {
      ...b,
      pct: (b.count / allCpi.length) * 100,
      cumPct: (cum / allCpi.length) * 100,
    }
  })
}

const WEEKDAY_NAMES = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun',
]

/**
 * Section 5.2: 星期对比
 */
export function calcWeekdayAnalysis(
  cohorts,
  targetDay,
  threshold,
  extraDays = []
) {
  const wdGroups = Array.from({ length: 7 }, () => [])
  for (const c of cohorts) {
    const wd = c.date.getDay()
    const idx = wd === 0 ? 6 : wd - 1
    wdGroups[idx].push(c)
  }

  const milestones = [0, 3, 7, 14, 30, 60]
    .filter((d) => d < targetDay)
    .concat([targetDay], extraDays)

  return wdGroups.map((gc, wd) => {
    let cleaned = gc
    for (const d of [7, targetDay]) {
      if (cleaned.length >= 4) {
        cleaned = iqrFilter(iqrFilter(cleaned, d), 7)
      }
    }
    const cpis = gc.filter((c) => c.cpi != null).map((c) => c.cpi)
    const cpiStr = cpis.length > 0 ? mean(cpis).toFixed(4) : '–'
    const means = {}
    for (const d of milestones) {
      const vals = cleaned
        .filter((c) => d in (c.day_roas || {}))
        .map((c) => c.day_roas[d])
      if (vals.length > 0) means[d] = mean(vals)
    }
    const evaluable = gc.filter((c) => targetDay in (c.day_roas || {}))
    const success = evaluable.filter(
      (c) => c.day_roas[targetDay] >= threshold
    )
    return {
      name: WEEKDAY_NAMES[wd],
      count: cleaned.length,
      cpi: cpiStr,
      means,
      evaluable: evaluable.length,
      successCount: success.length,
      rate: evaluable.length > 0 ? (success.length / evaluable.length) * 100 : 0,
      successMean:
        success.length > 0
          ? mean(success.map((c) => c.day_roas[targetDay])).toFixed(1) + '%'
          : '–',
      failedMean:
        evaluable.length - success.length > 0
          ? mean(
              evaluable
                .filter((c) => c.day_roas[targetDay] < threshold)
                .map((c) => c.day_roas[targetDay])
            ).toFixed(1) + '%'
          : '–',
    }
  })
}

/**
 * Section 5.3: 按周对比
 */
export function calcWeeklyAnalysis(
  cohorts,
  targetDay,
  threshold,
  extraDays = []
) {
  const weekGroups = new Map()
  for (const c of [...cohorts].sort((a, b) => a.date - b.date)) {
    const iso = getISOWeek(c.date)
    const key = `${iso.year}-W${String(iso.week).padStart(2, '0')}`
    if (!weekGroups.has(key)) weekGroups.set(key, [])
    weekGroups.get(key).push(c)
  }

  const milestones = [0, 3, 7, 14, 30, 60]
    .filter((d) => d < targetDay)
    .concat([targetDay], extraDays)

  return Array.from(weekGroups.entries()).map(([weekLabel, gc]) => {
    let cleaned = gc
    for (const d of [7, targetDay]) {
      if (cleaned.length >= 4) {
        cleaned = iqrFilter(iqrFilter(cleaned, d), 7)
      }
    }
    const totalInstalls = gc.reduce((s, c) => s + c.installs, 0)
    const cpis = gc.filter((c) => c.cpi != null).map((c) => c.cpi)
    const cpiStr = cpis.length > 0 ? mean(cpis).toFixed(4) : '–'
    const cells = {}
    for (const d of milestones) {
      const vals = cleaned
        .filter((c) => d in (c.day_roas || {}))
        .map((c) => c.day_roas[d])
      if (vals.length > 0) cells[d] = mean(vals)
    }
    const evaluable = gc.filter((c) => targetDay in (c.day_roas || {}))
    const nOk = evaluable.filter(
      (c) => c.day_roas[targetDay] >= threshold
    ).length
    const rateStr =
      evaluable.length > 0
        ? `${((nOk / evaluable.length) * 100).toFixed(0)}% (${nOk}/${evaluable.length})`
        : '–'
    return {
      weekLabel,
      count: gc.length,
      totalInstalls,
      cpi: cpiStr,
      cells,
      rateStr,
    }
  })
}

/**
 * Section 5.4.3: 工作日 vs 周末
 */
export function calcWorkdayComparison(
  cohorts,
  targetDay,
  threshold,
  extraDays = []
) {
  const weekdayPool = cohorts.filter((c) => {
    const wd = c.date.getDay()
    return wd >= 1 && wd <= 5 // Mon-Fri
  })
  const weekendPool = cohorts.filter((c) => {
    const wd = c.date.getDay()
    return wd === 0 || wd === 6 // Sun, Sat
  })

  const milestones = [0, 3, 7, 14, 30, 60]
    .filter((d) => d < targetDay)
    .concat([targetDay], extraDays)

  const calcStats = (pool) => {
    const stats = { count: pool.length, means: {}, rate: 0 }
    for (const d of milestones) {
      const vals = pool
        .filter((c) => d in (c.day_roas || {}))
        .map((c) => c.day_roas[d])
      if (vals.length > 0) stats.means[d] = mean(vals)
    }
    const evaluable = pool.filter((c) => targetDay in (c.day_roas || {}))
    const success = evaluable.filter((c) => c.day_roas[targetDay] >= threshold)
    stats.rate = evaluable.length > 0 ? (success.length / evaluable.length) * 100 : 0
    return stats
  }

  const wdStats = calcStats(weekdayPool)
  const weStats = calcStats(weekendPool)

  const rows = milestones.map((d) => ({
    label: `Day ${d} 均值`,
    weekday: wdStats.means[d],
    weekend: weStats.means[d],
    diff: weStats.means[d] != null && wdStats.means[d] != null ? weStats.means[d] - wdStats.means[d] : null,
  }))

  rows.push({
    label: '达标率',
    weekday: wdStats.rate,
    weekend: weStats.rate,
    diff: weStats.rate - wdStats.rate,
    isRate: true,
  })

  return {
    weekdayCount: weekdayPool.length,
    weekendCount: weekendPool.length,
    rows,
  }
}

/**
 * Section 5.6: 按月对比
 */
export function calcMonthlyAnalysis(
  cohorts,
  targetDay,
  threshold,
  extraDays = []
) {
  const monthGroups = new Map()
  for (const c of [...cohorts].sort((a, b) => a.date - b.date)) {
    const key = `${c.date.getFullYear()}-${String(c.date.getMonth() + 1).padStart(2, '0')}`
    if (!monthGroups.has(key)) monthGroups.set(key, [])
    monthGroups.get(key).push(c)
  }

  const milestones = [0, 3, 7, 14, 30, 60]
    .filter((d) => d < targetDay)
    .concat([targetDay], extraDays)

  return Array.from(monthGroups.entries()).map(([monthLabel, gc]) => {
    let cleaned = gc
    for (const d of [7, targetDay]) {
      if (cleaned.length >= 4) {
        cleaned = iqrFilter(iqrFilter(cleaned, d), 7)
      }
    }
    const totalInstalls = gc.reduce((s, c) => s + c.installs, 0)
    const cpis = gc.filter((c) => c.cpi != null).map((c) => c.cpi)
    const cpiStr = cpis.length > 0 ? mean(cpis).toFixed(4) : '–'
    const cells = {}
    for (const d of milestones) {
      const vals = cleaned
        .filter((c) => d in (c.day_roas || {}))
        .map((c) => c.day_roas[d])
      if (vals.length > 0) cells[d] = mean(vals)
    }
    const evaluable = gc.filter((c) => targetDay in (c.day_roas || {}))
    const nOk = evaluable.filter(
      (c) => c.day_roas[targetDay] >= threshold
    ).length
    const rateStr =
      evaluable.length > 0
        ? `${((nOk / evaluable.length) * 100).toFixed(0)}% (${nOk}/${evaluable.length})`
        : '–'
    return {
      monthLabel,
      count: gc.length,
      totalInstalls,
      cpi: cpiStr,
      cells,
      rateStr,
    }
  })
}

function getISOWeek(d) {
  const dt = new Date(d)
  dt.setHours(0, 0, 0, 0)
  const day = dt.getDay()
  const thursdayOffset = day >= 4 ? 4 - day : -3 - day
  dt.setDate(dt.getDate() + thursdayOffset)
  const yearStart = new Date(dt.getFullYear(), 0, 1)
  const weekNo = Math.ceil(((dt - yearStart) / 86400000 + 1) / 7)
  return { year: dt.getFullYear(), week: weekNo }
}

/**
 * Section 6: 近期队列表现
 * @returns {Array<{ date_str: string, cells: Record<number, string> }>}
 */
export function calcRecentCohorts(
  allCohorts,
  successful,
  targetDay,
  checkDays = [0, 3, 7, 14, 30].filter((d) => d < targetDay)
) {
  const pctThresholds = {}
  for (const d of checkDays) {
    const sVals = successful
      .filter((c) => d in (c.day_roas || {}))
      .map((c) => c.day_roas[d])
      .sort((a, b) => a - b)
    if (sVals.length === 0) continue
    pctThresholds[d] = [
      percentile(sVals, 10),
      percentile(sVals, 25),
      percentile(sVals, 50),
      percentile(sVals, 75),
      percentile(sVals, 90),
    ]
  }

  const recent = [...allCohorts]
    .sort((a, b) => b.date - a.date)
    .slice(0, 14)

  const bandLabel = (val, thresholds) => {
    const [p10, p25, p50, p75, p90] = thresholds
    if (val >= p90) return `${val.toFixed(1)}% (>P90)`
    if (val >= p75) return `${val.toFixed(1)}% (P75)`
    if (val >= p50) return `${val.toFixed(1)}% (P50)`
    if (val >= p25) return `${val.toFixed(1)}% (P25)`
    if (val >= p10) return `${val.toFixed(1)}% (P10)`
    return `${val.toFixed(1)}% (<P10)`
  }

  return recent.map((c) => {
    const cells = {}
    for (const d of checkDays) {
      if (d in (c.day_roas || {}) && d in pctThresholds) {
        cells[d] = bandLabel(c.day_roas[d], pctThresholds[d])
      } else {
        cells[d] = '–'
      }
    }
    return { date_str: c.date_str, cells }
  })
}

/**
 * 运行完整分析，返回各 Section 所需数据
 */
/**
 * 根据目标天数过滤 cohort，只保留有完整数据的
 * @param {Array} cohorts - 所有 cohort
 * @param {number} day - 目标天数
 * @returns {Array} 有该天数据的 cohort
 */
function filterCohortsByDay(cohorts, day) {
  // 只根据实际的数据量来判断：cohort 是否真的有该天的 ROAS 数据
  return cohorts.filter((c) => day in (c.day_roas || {}))
}

export function runAnalysis(
  dailyData,
  threshold,
  targetDay,
  excludeBefore,
  excludeRanges
) {
  const { shouldExclude, describe } = buildExcludeFn(
    excludeBefore,
    excludeRanges
  )
  const allCohorts = transformDailyData(dailyData)
  const cohorts = allCohorts.filter((c) => !shouldExclude(c.date))
  if (cohorts.length === 0) {
    return {
      cohorts: [],
      cTarget: [],
      successful: [],
      failed: [],
      dateMin: null,
      dateMax: null,
      excludeDesc: describe(),
      distribution: {},
      earlyMilestones: [],
      curveAll: [],
      curveSuccess: [],
      cpiBuckets: [],
      weekdayAnalysis: [],
      weeklyAnalysis: [],
      recentCohorts: [],
      cpiStats: null,
      successfulCpiRange: null,
    }
  }

  const dateMin = new Date(
    Math.min(...cohorts.map((c) => c.date.getTime()))
  )
  const dateMax = new Date(
    Math.max(...cohorts.map((c) => c.date.getTime()))
  )

  // 只使用有 targetDay 实际数据的 cohort 进行目标分析
  const cTargetRaw = filterCohortsByDay(cohorts, targetDay)
  let cTarget = iqrFilter(cTargetRaw, targetDay)
  cTarget = iqrFilter(cTarget, 7)

  const successful = cTarget
    .filter((c) => c.day_roas[targetDay] >= threshold)
    .sort((a, b) => a.day_roas[targetDay] - b.day_roas[targetDay])
  const failed = cTarget.filter((c) => c.day_roas[targetDay] < threshold)

  const lifecycleDay = 180
  const hasLifecycle =
    lifecycleDay > targetDay &&
    cohorts.some((c) => lifecycleDay in (c.day_roas || {}))

  // 每个分布只使用有该天实际数据的 cohort
  const dist0 = calcDistribution(filterCohortsByDay(cohorts, 0), 0)
  const dist30 = calcDistribution(filterCohortsByDay(cohorts, 30), 30)
  const distTarget = calcDistribution(cTarget, targetDay)

  const distribution = {
    day0: dist0,
    day30: dist30,
    targetDay: distTarget,
  }
  // 额外支持 120 与 180 分布（仅在存在数据时）
  for (const d of [120, lifecycleDay]) {
    const cohortsWithDay = filterCohortsByDay(cohorts, d)
    if (d > targetDay && cohortsWithDay.length > 0) {
      const dist = calcDistribution(cohortsWithDay, d)
      if (dist.length > 0) distribution[`day${d}`] = dist
    }
  }

  const earlyMilestones = calcEarlyMilestones(
    cTarget,
    successful,
    failed,
    targetDay,
    threshold
  )
  // 回收曲线（全部队列）使用所有经过日期排除的 cohort
  // 每个 milestone 会单独过滤有该天数据的 cohort
  const curveAll = calcRecoveryCurveAll(cohorts, targetDay)
  const curveSuccess = calcRecoveryCurveSuccess(
    successful,
    cTarget,
    targetDay,
    threshold
  )
  // 维度分析使用所有经过日期排除的 cohort（不限于有 targetDay 数据的）
  // 但在计算时会根据每个 milestone 的实际数据量进行过滤
  const cohortsForAnalysis = cohorts
  // CPI/LTV bucketSize 自动判断：median < 0.5 使用 0.02，否则使用 0.2
  const cpiBuckets = calcCpiBuckets(cohortsForAnalysis, cTarget, targetDay, threshold)
  const ltvBuckets = calcLtvBuckets(cohortsForAnalysis, cTarget, targetDay, threshold)
  const installsBuckets = calcInstallsBuckets(
    cohortsForAnalysis,
    cTarget,
    targetDay,
    threshold
  )
  const extraDays = hasLifecycle ? [lifecycleDay] : []
  const weekdayAnalysis = calcWeekdayAnalysis(
    cohortsForAnalysis,
    targetDay,
    threshold,
    extraDays
  )
  const workdayComparison = calcWorkdayComparison(
    cohortsForAnalysis,
    targetDay,
    threshold,
    extraDays
  )
  // 周/月分析使用经过日期排除过滤的 cohort
  const weeklyAnalysis = calcWeeklyAnalysis(
    cohorts,
    targetDay,
    threshold,
    extraDays
  )
  const monthlyAnalysis = calcMonthlyAnalysis(
    cohorts,
    targetDay,
    threshold,
    extraDays
  )

  const checkDays = [0, 3, 7, 14, 30].filter((d) => d < targetDay)
  const recentCohorts = calcRecentCohorts(
    allCohorts,
    successful,
    targetDay,
    checkDays
  )

  // CPI/LTV/Installs stats 只使用有 targetDay 数据的 cohort
  const allCpi = cohortsForAnalysis.filter((c) => c.cpi != null).map((c) => c.cpi)
  const cpiStats =
    allCpi.length > 0
      ? {
          mean: mean(allCpi),
          median: median(allCpi),
          count: allCpi.length,
        }
      : null

  const sCpi = successful
    .filter((c) => c.cpi != null)
    .map((c) => c.cpi)
    .sort((a, b) => a - b)
  const successfulCpiRange =
    sCpi.length > 0
      ? {
          min: Math.min(...sCpi),
          max: Math.max(...sCpi),
          median: median(sCpi),
        }
      : null

  const allLtv = cohortsForAnalysis.filter((c) => c.ltv != null).map((c) => c.ltv)
  const ltvStats =
    allLtv.length > 0
      ? {
          mean: mean(allLtv),
          median: median(allLtv),
          count: allLtv.length,
        }
      : null

  const sLtv = successful
    .filter((c) => c.ltv != null)
    .map((c) => c.ltv)
    .sort((a, b) => a - b)
  const successfulLtvRange =
    sLtv.length > 0
      ? {
          min: Math.min(...sLtv),
          max: Math.max(...sLtv),
          median: median(sLtv),
        }
      : null

  const allInstalls = cohortsForAnalysis.map((c) => c.installs)
  const installsStats =
    allInstalls.length > 0
      ? {
          mean: mean(allInstalls),
          median: median(allInstalls),
          count: allInstalls.length,
        }
      : null

  const sInst = successful.map((c) => c.installs).sort((a, b) => a - b)
  const successfulInstallsRange =
    sInst.length > 0
      ? {
          min: Math.min(...sInst),
          max: Math.max(...sInst),
          median: median(sInst),
        }
      : null

  return {
    cohorts,
    cTarget,
    successful,
    failed,
    dateMin,
    dateMax,
    excludeDesc: describe(),
    distribution,
    earlyMilestones,
    curveAll,
    curveSuccess,
    cpiBuckets,
    ltvBuckets,
    installsBuckets,
    weekdayAnalysis,
    workdayComparison,
    weeklyAnalysis,
    monthlyAnalysis,
    recentCohorts,
    checkDays,
    cpiStats,
    successfulCpiRange,
    ltvStats,
    successfulLtvRange,
    installsStats,
    successfulInstallsRange,
  }
}

/**
 * 生成 Markdown 格式的分析报告
 * @param {Object} analysis - runAnalysis 返回的分析结果
 * @param {number} threshold - ROAS 阈值
 * @param {number} targetDay - 目标天数
 * @param {Object} sourceInfo - 数据源信息 { appName, country, packageName }
 * @returns {string} Markdown 格式的报告
 */
export function generateMarkdownReport(analysis, threshold, targetDay, sourceInfo = { appName: 'Analysis' }) {
  const lines = []
  const p = (text) => lines.push(text)

  const {
    cohorts,
    cTarget,
    successful,
    failed,
    dateMin,
    dateMax,
    excludeDesc,
    distribution,
    earlyMilestones,
    curveAll,
    curveSuccess,
    cpiBuckets,
    ltvBuckets,
    installsBuckets,
    weekdayAnalysis,
    workdayComparison,
    weeklyAnalysis,
    monthlyAnalysis,
    recentCohorts,
    checkDays,
  } = analysis

  if (!cohorts || cohorts.length === 0) {
    return '# ROAS 回收曲线基准分析\n\n> 没有有效的队列数据。'
  }

  const formatDate = (d) => {
    if (!d) return '–'
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  const lifecycleDay = 180
  const hasLifecycle = lifecycleDay > targetDay && curveAll.some((c) => c.day === lifecycleDay)

  // 构建标题：ROAS-{app_name}-{package_name}-{country} 回收曲线基准分析
  const { appName, country, packageName } = sourceInfo
  const titleParts = ['ROAS']
  if (appName) titleParts.push(appName)
  if (packageName) titleParts.push(packageName)
  if (country) titleParts.push(country)
  const title = titleParts.join('-')

  // Header
  p(`# ${title} 回收曲线基准分析`)
  p('')
  p(`> **目标**: Day ${targetDay} ROAS >= ${threshold}%  `)
  p(`> **日期范围**: ${formatDate(dateMin)} ~ ${formatDate(dateMax)}  `)
  p(`> **筛选后队列数**: ${cohorts.length}  `)
  p(`> **排除规则**: ${excludeDesc || '–'}  `)
  p(`> **版本**: V${VERSION}  `)
  p('')

  // Section 1 — ROAS distributions
  p('## 1. ROAS 分布')
  p('')

  const printDistribution = (dist, day, sectionLabel) => {
    if (!dist || dist.length === 0) return

    const vals = []
    dist.forEach((b) => {
      for (let i = 0; i < b.count; i++) vals.push((b.low + b.high) / 2)
    })
    const meanV = mean(vals)
    const medV = median(vals)

    p(`### ${sectionLabel} Day ${day} ROAS 分布`)
    p('')
    p(`有效队列: **${dist.reduce((sum, b) => sum + b.count, 0)}** · 均值: **${meanV.toFixed(1)}%** · 中位数: **${medV.toFixed(1)}%**`)
    p('')

    const maxBar = 30
    const peak = Math.max(...dist.map((b) => b.count))

    p('| 区间 | 数量 | 占比 | 累计 | 分布 |')
    p('|-----:|-----:|-----:|-----:|:-----|')
    let cum = 0
    const total = dist.reduce((sum, b) => sum + b.count, 0)
    dist.forEach((b) => {
      cum += b.count
      const pct = (b.count / total) * 100
      const cumPct = (cum / total) * 100
      const bar = '█'.repeat(Math.round((b.count / peak) * maxBar))
      const marker = b.has100 ? ' ◀100%' : ''
      p(`| ${b.low}%–${b.high}% | ${b.count} | ${pct.toFixed(1)}% | ${cumPct.toFixed(1)}% | ${bar}${marker} |`)
    })
    p('')
  }

  const distDays = [
    { day: 0, data: distribution.day0, label: '1.1' },
    { day: 30, data: distribution.day30, label: '1.2' },
    { day: targetDay, data: distribution.targetDay, label: '1.3' },
  ]
  if (distribution.day120) {
    distDays.push({ day: 120, data: distribution.day120, label: '1.4' })
  }
  if (distribution.day180) {
    distDays.push({ day: 180, data: distribution.day180, label: '1.5' })
  }

  distDays.forEach(({ day, data, label }) => {
    printDistribution(data, day, label)
  })

  const cTargetRawCount = cTarget.length + (cohorts.filter((c) => c.day_roas[targetDay] != null).length - cTarget.length)
  const iqrRemovedCount = cTargetRawCount - cTarget.length
  if (iqrRemovedCount > 0) {
    p(`> **IQR 过滤**: 从 ${cTargetRawCount} 个有 Day ${targetDay} 数据的队列中移除 **${iqrRemovedCount}** 个异常值 → 剩余 **${cTarget.length}**  `)
    p('')
  }

  // Section 2 — Early milestone thresholds
  p('## 2. 早期达标基准线')
  p('')
  p(`> 以下基准用于判断新队列是否 on-track（目标: Day ${targetDay} >= ${threshold}%）  `)
  p(`> 成功组: **${successful.length}** 个队列 · 失败组: **${failed.length}** 个队列`)
  p('')

  if (earlyMilestones && earlyMilestones.length > 0) {
    p('| 里程碑 | 达标线（成功 P5–P15） | 成功组均值 | 优秀线（成功 P70–P80） | 达标线命中率 | 未达标追回率 | 整体准确率 |')
    p('|-------:|--------------------:|-----------:|--------------------:|------------:|------------:|----------:|')

    earlyMilestones.forEach((m) => {
      const thresholdVal = m.threshold ?? 0
      const meanVal = m.mean ?? 0
      const excellentVal = m.excellent ?? 0
      const hitRateVal = m.hitRate ?? 0
      const hitAboveVal = m.hitAbove ?? 0
      const hitTotalVal = m.hitTotal ?? 0
      const recoverRateVal = m.recoverRate ?? 0
      const recoverOkVal = m.recoverOk ?? 0
      const recoverTotalVal = m.recoverTotal ?? 0
      const accuracyVal = m.accuracy ?? 0
      p(`| Day ${m.day} | ${thresholdVal.toFixed(1)}% | ${meanVal.toFixed(1)}% | ${excellentVal.toFixed(1)}% | ${hitRateVal.toFixed(0)}% (${hitAboveVal}/${hitTotalVal}) | ${recoverRateVal.toFixed(0)}% (${recoverOkVal}/${recoverTotalVal}) | ${accuracyVal.toFixed(1)}% |`)
    })
    p('')
  }

  p('> **达标线**（成功 P5–P15 均值）: 成功队列底部区间的平均值，低于此值极少成功  ')
  p('> **优秀线**（成功 P70–P80 均值）: 成功队列上游区间的平均值，达到即为优秀  ')
  p('> **达标线命中率**: Day X >= 达标线时，最终达成目标的概率  ')
  p('> **未达标追回率**: Day X < 达标线时，最终仍达成目标的概率  ')
  p('> **整体准确率**: (正确预测成功 + 正确预测失败) / 全部队列')
  p('')

  // Section 3 — Recovery curve (all cohorts)
  p('## 3. 回收曲线（全量数据分布）')
  p('')
  p('> 以下为**全部队列**在各里程碑的 ROAS 百分位分布（不区分成功/失败）  ')
  p('> 有效队列数按各 Day 实际有数据的队列统计')
  p('')

  if (curveAll && curveAll.length > 0) {
    p('| 里程碑 | 队列数 | P10 | P25 | P50 | P75 | P90 | 均值 |')
    p('|-------:|-------:|----:|----:|----:|----:|----:|-----:|')

    curveAll.forEach((c) => {
      p(`| Day ${c.day} | ${c.count} | ${c.p10.toFixed(1)}% | ${c.p25.toFixed(1)}% | ${c.p50.toFixed(1)}% | ${c.p75.toFixed(1)}% | ${c.p90.toFixed(1)}% | ${c.mean.toFixed(1)}% |`)
    })
    p('')
  }

  p('> **对比 Section 4 成功队列分布**: 全量 P50 vs 成功队列 P50 的差距反映筛选偏差')
  p('')

  // Section 4 — Recovery curve (successful cohorts)
  p('## 4. 回收曲线基准（成功队列分布）')
  p('')
  p(`> 以下为成功队列在各里程碑的 ROAS 百分位分布  `)
  p(`> 成功组: **${successful.length}** 个队列（Day ${targetDay} >= ${threshold}%）`)
  p('')

  if (curveSuccess && curveSuccess.length > 0) {
    p('| 里程碑 | P10 | P25 | P50 | P75 | P90 | P25 命中率 | P50 命中率 |')
    p('|-------:|----:|----:|----:|----:|----:|----------:|----------:|')

    curveSuccess.forEach((c) => {
      p(`| Day ${c.day} | ${c.p10.toFixed(1)}% | ${c.p25.toFixed(1)}% | ${c.p50.toFixed(1)}% | ${c.p75.toFixed(1)}% | ${c.p90.toFixed(1)}% | ${c.hitP25.toFixed(0)}% | ${c.hitP50.toFixed(0)}% |`)
    })
    p('')

    p('> **P50**: 中位数，代表典型成功队列的表现  ')
    p('> **P10–P90**: 成功队列的正常波动范围  ')
    p(`> **P25/P50 命中率**: Day X >= 该分位值时，最终达成 Day ${targetDay} >= ${threshold}% 的概率  `)
    p('> **用法**: 新队列各阶段 ROAS 落在 P25–P75 之间即为健康')
    p('')
  }

  // Section 5 — Dimension analysis
  p('## 5. 维度分析')
  p('')
  p('> **数据范围说明**  ')
  p('> 5.1–5.4 使用过滤后数据（排除节假日 + IQR 异常值），与 Section 1–4 一致  ')
  p('> 5.5 按周对比、5.6 按月对比使用全量数据（不过滤），用于观察时间趋势')
  p('')

  // 5.1 CPI distribution
  if (cpiBuckets && cpiBuckets.length > 0) {
    p('### 5.1 CPI 分析')
    p('')

    // 计算 CPI 统计信息
    const allCpi = cohorts.filter((c) => c.cpi != null).map((c) => c.cpi)
    if (allCpi.length > 0) {
      const cpiMeanVal = mean(allCpi)
      const cpiMedVal = median(allCpi)
      p(`有效队列: **${allCpi.length}** · 均值: **${cpiMeanVal.toFixed(4)}** · 中位数: **${cpiMedVal.toFixed(4)}**`)
      p('')
    }

    p('| CPI 区间 | 队列数 | 占比 | 累计 | 可评估 | 成功 | 成功率 |')
    p('|---------:|-------:|-----:|-----:|-------:|-----:|-------:|')

    cpiBuckets.forEach((b) => {
      p(`| $${b.low.toFixed(2)}–$${b.high.toFixed(2)} | ${b.count} | ${b.pct.toFixed(1)}% | ${b.cumPct.toFixed(1)}% | ${b.evaluable} | ${b.success} | ${b.rateStr} |`)
    })
    p('')

    // 成功组 CPI 统计
    const sCpi = successful.filter((c) => c.cpi != null).map((c) => c.cpi)
    if (sCpi.length > 0) {
      const sCpiMin = Math.min(...sCpi)
      const sCpiMax = Math.max(...sCpi)
      const sCpiMed = median(sCpi)
      p(`> **建议目标 CPI**: ≤ **${sCpiMax.toFixed(4)}**（成功组最大值）  `)
      p(`> 成功组 CPI 范围: ${sCpiMin.toFixed(4)} ~ ${sCpiMax.toFixed(4)}，中位数 ${sCpiMed.toFixed(4)}  `)
    } else {
      p('> 成功队列不足，无法给出目标 CPI 建议  ')
    }
    p(`> 成功/失败定义同 Section 2（Day ${targetDay} >= ${threshold}%）`)
    p('')
  }

  // 5.2 LTV distribution
  if (ltvBuckets && ltvBuckets.length > 0) {
    p('### 5.2 LTV 分析')
    p('')

    // 计算 LTV 统计信息
    const allLtv = cohorts.filter((c) => c.ltv != null).map((c) => c.ltv)
    if (allLtv.length > 0) {
      const ltvMeanVal = mean(allLtv)
      const ltvMedVal = median(allLtv)
      p(`有效队列: **${allLtv.length}** · 均值: **${ltvMeanVal.toFixed(4)}** · 中位数: **${ltvMedVal.toFixed(4)}**`)
      p('')
    }

    p('| LTV 区间 | 队列数 | 占比 | 累计 | 可评估 | 成功 | 成功率 |')
    p('|---------:|-------:|-----:|-----:|-------:|-----:|-------:|')

    ltvBuckets.forEach((b) => {
      p(`| $${b.low.toFixed(2)}–$${b.high.toFixed(2)} | ${b.count} | ${b.pct.toFixed(1)}% | ${b.cumPct.toFixed(1)}% | ${b.evaluable} | ${b.success} | ${b.rateStr} |`)
    })
    p('')

    // 成功组 LTV 统计
    const sLtv = successful.filter((c) => c.ltv != null).map((c) => c.ltv)
    if (sLtv.length > 0) {
      const sLtvMin = Math.min(...sLtv)
      const sLtvMax = Math.max(...sLtv)
      const sLtvMed = median(sLtv)
      p(`> 成功组 LTV 范围: ${sLtvMin.toFixed(4)} ~ ${sLtvMax.toFixed(4)}，中位数 ${sLtvMed.toFixed(4)}  `)
    }
    p(`> 成功/失败定义同 Section 2（Day ${targetDay} >= ${threshold}%）`)
    p('')
  }

  // 5.3 Installs distribution
  if (installsBuckets && installsBuckets.length > 0) {
    p('### 5.3 安装量分析')
    p('')

    // 计算安装量统计信息
    const allInstalls = cohorts.map((c) => c.installs)
    if (allInstalls.length > 0) {
      const instMeanVal = mean(allInstalls)
      const instMedVal = median(allInstalls)
      p(`有效队列: **${allInstalls.length}** · 均值: **${instMeanVal.toFixed(0)}** · 中位数: **${instMedVal.toFixed(0)}**`)
      p('')
    }

    p('| 安装量区间 | 队列数 | 占比 | 累计 | 可评估 | 成功 | 成功率 |')
    p('|----------:|-------:|-----:|-----:|-------:|-----:|-------:|')

    installsBuckets.forEach((b) => {
      p(`| ${b.low}–${b.high} | ${b.count} | ${b.pct.toFixed(1)}% | ${b.cumPct.toFixed(1)}% | ${b.evaluable} | ${b.success} | ${b.rateStr} |`)
    })
    p('')

    // 成功组安装量统计
    const sInst = successful.map((c) => c.installs)
    if (sInst.length > 0) {
      const sInstMin = Math.min(...sInst)
      const sInstMax = Math.max(...sInst)
      const sInstMed = median(sInst)
      p(`> 成功组安装量范围: ${sInstMin} ~ ${sInstMax}，中位数 ${sInstMed.toFixed(0)}  `)
    }
    p(`> 成功/失败定义同 Section 2（Day ${targetDay} >= ${threshold}%）`)
    p('')
  }

  // 5.4 Weekday analysis
  if (weekdayAnalysis && weekdayAnalysis.length > 0) {
    const milestones = weekdayAnalysis[0]?.means ? Object.keys(weekdayAnalysis[0].means).map(Number).sort((a, b) => a - b) : []
    const msHeaders = milestones.map((d) => `Day ${d}`).join(' | ')
    const msSep = milestones.map(() => '----:').join(' | ')

    p('### 5.4 周几对比')
    p('')

    p('#### 5.4.1 各星期平均 ROAS')
    p('')
    p(`| 星期 | 队列 | CPI | ${msHeaders} |`)
    p(`|-----:|-----:|----:| ${msSep} |`)

    // 收集每天的最高/最低数据
    const wdMeans = {}

    weekdayAnalysis.forEach((wd) => {
      const cells = milestones.map((d) => {
        const val = wd.means?.[d]
        if (val != null) {
          if (!wdMeans[d]) wdMeans[d] = {}
          wdMeans[d][wd.name] = val
          return `${val.toFixed(1)}%`
        }
        return '–'
      }).join(' | ')
      p(`| ${wd.name} | ${wd.count} | ${wd.cpi} | ${cells} |`)
    })
    p('')

    // 输出每天的最高/最低分析（每个 Day 作为一个独立的引用块段落）
    milestones.forEach((d) => {
      const dayData = wdMeans[d]
      if (!dayData) return

      const entries = Object.entries(dayData).filter(([_, v]) => v != null)
      if (entries.length < 2) return

      const best = entries.reduce((max, [name, val]) => val > max.val ? { name, val } : max, { name: entries[0][0], val: entries[0][1] })
      const worst = entries.reduce((min, [name, val]) => val < min.val ? { name, val } : min, { name: entries[0][0], val: entries[0][1] })
      const gap = best.val - worst.val

      p(`> Day ${d}: 最高 **${best.name}** (${best.val.toFixed(1)}%) · 最低 **${worst.name}** (${worst.val.toFixed(1)}%) · 差距 ${gap.toFixed(1)}pp`)
      p('>')
    })
    p('')

    // 各星期达标率表格
    p(`#### 5.4.2 各星期达标率（Day ${targetDay} >= ${threshold}%）`)
    p('')
    p('| 星期 | 可评估 | 达标 | 达标率 | 达标均值 | 未达标均值 |')
    p('|-----:|-------:|-----:|-------:|---------:|-----------:|')

    weekdayAnalysis.forEach((wd) => {
      const evaluable = wd.evaluable || 0
      const successCount = wd.successCount || 0
      const rate = wd.rate || 0
      const successMean = wd.successMean || '–'
      const failedMean = wd.failedMean || '–'
      const rateStr = evaluable > 0 ? `${rate.toFixed(1)}%` : '–'
      p(`| ${wd.name} | ${evaluable} | ${successCount} | ${rateStr} | ${successMean} | ${failedMean} |`)
    })
    p('')
  }

  // 5.4.3 Workday vs Weekend
  if (workdayComparison && workdayComparison.rows && workdayComparison.rows.length > 0) {
    p('#### 5.4.3 工作日 vs 周末')
    p('')

    const { weekdayCount, weekendCount, rows } = workdayComparison

    // 表头
    p('| 指标 | 工作日（周一至周五） | 周末（周六周日） | 差距 |')
    p('|:-----|:--------------------:|:----------------:|:----:|')

    // 队列数行
    p(`| 队列数 | ${weekdayCount} | ${weekendCount} | – |`)

    // 各 Day 均值行
    rows.forEach((row) => {
      if (row.label && row.label.startsWith('Day')) {
        const wdVal = row.weekday != null ? `${row.weekday.toFixed(1)}%` : '–'
        const weVal = row.weekend != null ? `${row.weekend.toFixed(1)}%` : '–'
        const diffVal = row.diff != null ? `${(row.diff >= 0 ? '+' : '')}${row.diff.toFixed(1)}pp` : '–'
        p(`| ${row.label} | ${wdVal} | ${weVal} | ${diffVal} |`)
      }
    })

    // 达标率行
    const rateRow = rows.find((r) => r.label === '达标率')
    if (rateRow) {
      const wdRate = rateRow.weekday != null ? `${rateRow.weekday.toFixed(1)}%` : '–'
      const weRate = rateRow.weekend != null ? `${rateRow.weekend.toFixed(1)}%` : '–'
      const diffRate = rateRow.diff != null ? `${(rateRow.diff >= 0 ? '+' : '')}${rateRow.diff.toFixed(1)}pp` : '–'
      p(`| 达标率 | ${wdRate} | ${weRate} | ${diffRate} |`)
    }
    p('')
  }

  // 5.6 Weekly analysis
  if (weeklyAnalysis && weeklyAnalysis.length > 0) {
    // 从第一个元素的cells获取所有可能的里程碑（合并所有周的里程碑）
    const allDays = new Set()
    weeklyAnalysis.forEach((wk) => {
      Object.keys(wk.cells || {}).forEach((d) => allDays.add(Number(d)))
    })
    const milestones = Array.from(allDays).sort((a, b) => a - b)

    p('### 5.5 按周对比')
    p('')

    if (milestones.length === 0) {
      // 无里程碑数据，简化表格
      p('| 周 | 队列数 | 安装量 | CPI | 达标率 |')
      p('|---:|-------:|-------:|----:|-------:|')
      weeklyAnalysis.forEach((wk) => {
        const rateStr = wk.rateStr || '–'
        p(`| ${wk.weekLabel} | ${wk.count} | ${wk.totalInstalls} | ${wk.cpi} | ${rateStr} |`)
      })
    } else {
      // 有里程碑数据，完整表格 - 所有行格式必须完全一致
      const msHeaders = milestones.map((d) => `Day ${d}`).join(' | ')
      const msSep = milestones.map(() => '----:').join(' | ')

      // 表头（msHeaders 前后有空格）
      p(`| 周 | 队列数 | 安装量 | CPI | ${msHeaders} | 达标率 |`)
      // 分隔符（msSep 前后必须也有空格，与表头一致）
      p(`|---:|-------:|-------:|----:| ${msSep} |-------:|`)

      weeklyAnalysis.forEach((wk) => {
        const cells = milestones.map((d) => (wk.cells?.[d] != null ? `${wk.cells[d].toFixed(1)}%` : '–')).join(' | ')
        const rateStr = wk.rateStr || '–'
        // 数据行（cells 前后有空格，与表头一致）
        p(`| ${wk.weekLabel} | ${wk.count} | ${wk.totalInstalls} | ${wk.cpi} | ${cells} | ${rateStr} |`)
      })
    }
    p('')
  }

  // 5.7 Monthly analysis
  if (monthlyAnalysis && monthlyAnalysis.length > 0) {
    // 从所有月份的cells获取所有可能的里程碑（合并所有月的里程碑）
    const allDays = new Set()
    monthlyAnalysis.forEach((mo) => {
      Object.keys(mo.cells || {}).forEach((d) => allDays.add(Number(d)))
    })
    const milestones = Array.from(allDays).sort((a, b) => a - b)

    p('### 5.6 按月对比')
    p('')

    if (milestones.length === 0) {
      // 无里程碑数据，简化表格
      p('| 月份 | 队列数 | 安装量 | CPI | 达标率 |')
      p('|-----:|-------:|-------:|----:|-------:|')
      monthlyAnalysis.forEach((mo) => {
        const rateStr = mo.rateStr || '–'
        p(`| ${mo.monthLabel} | ${mo.count} | ${mo.totalInstalls} | ${mo.cpi} | ${rateStr} |`)
      })
    } else {
      // 有里程碑数据，完整表格
      const msHeaders = milestones.map((d) => `Day ${d}`).join(' | ')
      const msSep = milestones.map(() => '----:').join(' | ')

      // 表头
      p(`| 月份 | 队列数 | 安装量 | CPI | ${msHeaders} | 达标率 |`)
      // 分隔符 - 必须与表头列数完全一致
      p(`|-----:|-------:|-------:|----:| ${msSep} |-------:|`)

      monthlyAnalysis.forEach((mo) => {
        const cells = milestones.map((d) => (mo.cells?.[d] != null ? `${mo.cells[d].toFixed(1)}%` : '–')).join(' | ')
        const rateStr = mo.rateStr || '–'
        // 数据行 - 必须与表头格式一致
        p(`| ${mo.monthLabel} | ${mo.count} | ${mo.totalInstalls} | ${mo.cpi} | ${cells} | ${rateStr} |`)
      })
    }
    p('')
  }

  // Section 6 — Recent cohorts
  if (recentCohorts && recentCohorts.length > 0 && checkDays && checkDays.length > 0) {
    const dayHeaders = checkDays.map((d) => `Day ${d}`).join(' | ')
    const sepLine = checkDays.map(() => '----:').join(' | ')

    p('## 6. 近 14 天队列表现')
    p('')
    p('> 对照 Section 4 百分位基准，标注每个队列各里程碑所处分位')
    p('')
    p(`| 日期 | ${dayHeaders} |`)
    p(`|-----:| ${sepLine} |`)

    recentCohorts.forEach((c) => {
      const cells = checkDays
        .map((d) => {
          const val = c.cells?.[d]
          if (!val || val === '–') return '–'
          return val
        })
        .join(' | ')
      p(`| ${c.date_str} | ${cells} |`)
    })
    p('')
  }

  return lines.join('\n')
}
